import React, { useEffect, useState } from 'react';
import { landlordAPI } from '../../../lib/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Switch } from '../../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';

export const TenantNoteDialog = ({ open, onOpenChange, editingDoc, tenantId, onSaved }) => {
  const [noteForm, setNoteForm] = useState({ name: '', content: '', visible_to_tenant: true });
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    if (open) {
      if (editingDoc) {
        setNoteForm({
          name: editingDoc.name || '',
          content: editingDoc.content || '',
          visible_to_tenant: editingDoc.visible_to_tenant !== false,
        });
      } else {
        setNoteForm({ name: '', content: '', visible_to_tenant: true });
      }
    }
  }, [open, editingDoc]);

  const handleSaveNote = async () => {
    const name = noteForm.name.trim() || 'Untitled Note';
    setSavingNote(true);
    try {
      if (editingDoc) {
        await landlordAPI.updateTenantDocument(editingDoc.doc_id, {
          name,
          content: noteForm.content,
          visible_to_tenant: noteForm.visible_to_tenant,
        });
        toast.success('Note updated');
      } else {
        await landlordAPI.createTenantNote(tenantId, {
          name,
          content: noteForm.content,
          visible_to_tenant: noteForm.visible_to_tenant,
        });
        toast.success('Note created');
      }
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="note-composer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-5 h-5" />
            {editingDoc ? 'Edit Note' : 'New Note'}
          </DialogTitle>
          <DialogDescription>
            Compose a free-form text note. It's saved as a downloadable document for this tenant.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="note-name">Title</Label>
            <Input
              id="note-name"
              placeholder="e.g., Move-in walkthrough notes"
              value={noteForm.name}
              onChange={(e) => setNoteForm({ ...noteForm, name: e.target.value })}
              data-testid="note-title-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="note-content">Content</Label>
            <Textarea
              id="note-content"
              placeholder="Start typing your note here..."
              value={noteForm.content}
              onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
              rows={18}
              className="font-serif text-base leading-relaxed resize-y min-h-[400px] bg-white dark:bg-slate-900"
              data-testid="note-content-textarea"
            />
            <p className="text-xs text-muted-foreground">
              {noteForm.content.length.toLocaleString()} characters · saved as plain-text document
            </p>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
            <div>
              <Label htmlFor="note-visible" className="text-sm font-medium cursor-pointer">
                Visible to tenant
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                When on, the tenant can view and download this note from their portal.
              </p>
            </div>
            <Switch
              id="note-visible"
              checked={noteForm.visible_to_tenant}
              onCheckedChange={(checked) => setNoteForm({ ...noteForm, visible_to_tenant: checked })}
              data-testid="note-visibility-toggle"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={savingNote}>Cancel</Button>
          <Button onClick={handleSaveNote} disabled={savingNote} data-testid="save-note-btn">
            {savingNote ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : (editingDoc ? 'Update Note' : 'Save Note')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
